import React from "react";
import {
  Sparkles,
  Edit,
  Calendar,
  Clock,
  Briefcase,
  User,
  CheckSquare,
  Mail,
  MessageSquare,
  FileText,
} from "lucide-react";

// Icon mapping for dynamic rendering
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Sparkles,
  Edit,
  Calendar,
  Clock,
  Briefcase,
  User,
  CheckSquare,
  Mail,
  MessageSquare,
  FileText,
};

// Formatter functions mapping
const formatters: Record<string, (value: any) => string> = {
  hours: (value: any) => `${value}h`,
  date: (value: any) => {
    try {
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateOnly = new Date(date);
      dateOnly.setHours(0, 0, 0, 0);

      if (dateOnly.getTime() === today.getTime()) return "Today";

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (dateOnly.getTime() === yesterday.getTime()) return "Yesterday";

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (dateOnly.getTime() === tomorrow.getTime()) return "Tomorrow";

      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(value);
    }
  },
};

interface FieldMetadata {
  displayKey: string;
  label: string;
  icon: string;
  colorClass: {
    light: string;
    dark: string;
  };
  format?: string; // Format function name (e.g., 'hours', 'date')
  type: "text" | "number" | "date" | "entity" | "duration";
}

interface RecommendationCardProps {
  message: string;
  recommendation: {
    suggestedAction: {
      action: string;
      parameters: Record<string, any>;
      confidence: number;
    };
    alternatives: Array<{
      label: string;
      field: string;
      action: "modify" | "remove";
    }>;
    patternExplanation: string;
    availableOptions?: {
      projects?: Array<{ id: string; label: string; metadata?: any }>;
      clients?: Array<{ id: string; label: string; metadata?: any }>;
      tasks?: Array<{ id: string; label: string; metadata?: any }>;
    };
    // NEW: Field metadata for dynamic rendering
    fieldMetadata?: FieldMetadata[];
  };
  parameters: Record<string, any>;
  onConfirm: () => void;
  onModify: (field: string) => void;
  onManual: () => void;
  isDark?: boolean;
}

export const RecommendationCard: React.FC<RecommendationCardProps> = ({
  message,
  recommendation,
  parameters,
  onConfirm,
  onModify,
  onManual,
  isDark = false,
}) => {
  // Debug logging
  console.log("[RecommendationCard] Rendering with:", {
    hasMessage: !!message,
    hasRecommendation: !!recommendation,
    hasSuggestedAction: !!recommendation?.suggestedAction,
    hasFieldMetadata: !!recommendation?.fieldMetadata,
    fieldMetadataCount: recommendation?.fieldMetadata?.length || 0,
    hasAlternatives: !!recommendation?.alternatives,
    alternativesCount: recommendation?.alternatives?.length || 0,
    parameters: parameters,
    parametersKeys: Object.keys(parameters || {}),
  });

  return (
    <div
      className={`relative rounded-lg border ${
        isDark ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      } p-4 space-y-4`}
    >
      {/* Header with sparkle icon */}
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 p-2 rounded ${
            isDark
              ? "bg-success-900/50 text-success-300"
              : "bg-success-100 text-success-600"
          }`}
        >
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <h4
            className={`font-semibold text-sm mb-1 ${
              isDark ? "text-success-200" : "text-gray-900"
            }`}
          >
            Smart Suggestion
          </h4>
          <p
            className={`text-sm ${isDark ? "text-success-300" : "text-gray-700"}`}
          >
            {message}
          </p>
        </div>
      </div>

      {/* Pattern explanation (small muted text) */}
      <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-700"}`}>
        {recommendation.patternExplanation}
      </p>

      {/* Preview of suggested values - Dynamic rendering based on fieldMetadata */}
      <div className="flex flex-wrap gap-2">
        {recommendation.fieldMetadata &&
        recommendation.fieldMetadata.length > 0 ? (
          // NEW: Dynamic rendering using fieldMetadata
          recommendation.fieldMetadata.map((fieldMeta, idx) => {
            const value = parameters[fieldMeta.displayKey];
            const IconComponent = iconMap[fieldMeta.icon] || User;

            if (value) {
              // Use formatter if specified, otherwise use default string conversion
              const formatter = fieldMeta.format
                ? formatters[fieldMeta.format]
                : undefined;
              const formattedValue = formatter
                ? formatter(value)
                : String(value);
              return (
                <div
                  key={idx}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    isDark
                      ? fieldMeta.colorClass.dark
                      : fieldMeta.colorClass.light
                  }`}
                >
                  <IconComponent className="w-3 h-3" />
                  {formattedValue}
                </div>
              );
            }

            // Show available options count if field is missing but options exist
            if (fieldMeta.type === "entity") {
              const optionKey = fieldMeta.displayKey.includes("project")
                ? "projects"
                : fieldMeta.displayKey.includes("client")
                  ? "clients"
                  : fieldMeta.displayKey.includes("task")
                    ? "tasks"
                    : null;

              if (
                optionKey &&
                recommendation.availableOptions?.[
                  optionKey as keyof typeof recommendation.availableOptions
                ]?.length
              ) {
                const options = recommendation.availableOptions[
                  optionKey as keyof typeof recommendation.availableOptions
                ] as Array<any>;
                return (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <IconComponent className="w-3 h-3" />
                    {options.length} {fieldMeta.label.toLowerCase()} available
                  </div>
                );
              }
            }

            return null;
          })
        ) : (
          // FALLBACK: Legacy hardcoded rendering for backward compatibility
          <>
            {parameters.project_name && (
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                  isDark
                    ? "bg-accent-900/30 text-accent-300"
                    : "bg-accent-100 text-accent-700"
                }`}
              >
                <Briefcase className="w-3 h-3" />
                {parameters.project_name}
              </div>
            )}
            {!parameters.project_name &&
              recommendation.availableOptions?.projects &&
              recommendation.availableOptions.projects.length > 0 && (
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <Briefcase className="w-3 h-3" />
                  {recommendation.availableOptions.projects.length} projects
                  available
                </div>
              )}
            {parameters.hours && (
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                  isDark
                    ? "bg-purple-900/30 text-purple-300"
                    : "bg-purple-100 text-purple-700"
                }`}
              >
                <Clock className="w-3 h-3" />
                {parameters.hours}h
              </div>
            )}
            {parameters.date && (
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                  isDark
                    ? "bg-primary-900/30 text-primary-300"
                    : "bg-primary-100 text-primary-700"
                }`}
              >
                <Calendar className="w-3 h-3" />
                {formatDateDisplay(parameters.date)}
              </div>
            )}
            {parameters.client_name && (
              <div
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                  isDark
                    ? "bg-indigo-900/30 text-indigo-300"
                    : "bg-indigo-100 text-indigo-700"
                }`}
              >
                <User className="w-3 h-3" />
                {parameters.client_name}
              </div>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        {/* Primary confirm button */}
        <button
          onClick={onConfirm}
          className={`w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
            isDark
              ? "bg-success-600 hover:bg-success-700 text-white"
              : "bg-success-500 hover:bg-success-600 text-white"
          }`}
        >
          ✓ Confirm & Create
        </button>

        {/* Secondary alternative buttons */}
        <div className="flex flex-wrap gap-2">
          {recommendation.alternatives.map((alt, idx) => (
            <button
              key={idx}
              onClick={() => onModify(alt.field)}
              className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              }`}
            >
              <Edit className="w-3 h-3 inline mr-1" />
              {alt.label}
            </button>
          ))}
          <button
            onClick={onManual}
            className={`flex-1 py-1.5 px-3 rounded text-xs font-medium transition-colors ${
              isDark
                ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            Do it manually
          </button>
        </div>
      </div>

      {/* Confidence indicator (subtle) */}
      <div className="flex items-center justify-between text-xs">
        <span className={isDark ? "text-gray-500" : "text-gray-600"}>
          Confidence:{" "}
          {Math.round(recommendation.suggestedAction.confidence * 100)}%
        </span>
      </div>
    </div>
  );
};

function formatDateDisplay(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    if (dateOnly.getTime() === today.getTime()) {
      return "Today";
    }

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dateOnly.getTime() === yesterday.getTime()) {
      return "Yesterday";
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (dateOnly.getTime() === tomorrow.getTime()) {
      return "Tomorrow";
    }

    // Format as "Mon, Jan 15"
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}
